// M4-B §8.4: the negative-price day (20 Apr 2020) and the storage trade around it.
//   (1) sales below zero PAY |price| × bbl (production.netback already sees the hub price, gross < 0);
//   (2) auto-sell pauses below zero (production.tickSales' negative-netback rule) — barrels go to storage;
//   (3) storage full + no shut-in → forced sale at the negative price (production.storeOil → m4Overflow, M4-B hunk);
//   (4) a shut-in costs shutInRestartCostPerWell on restart and 2% reservoir damage on shale wells;
//   (5) a Cushing tank lease (day rate ×3 in Apr 2020) earns the contango (cash-and-carry, forward locked at signing);
//   (6) NYMEX futures expiring that day settle at −$37.63 (m3/hedging.markFuture, M4-B hunk → m4FuturesSettle);
//   company.stats.negativeDayHeld = 1 if the player had North American barrels and sold none below zero.
import { NEGATIVE_PRICE, dayRateM4, lerpAt } from '../../data/m4/rules.js';
import { allWells } from '../indexes.js';
import { eraDollars, fieldDef, yf } from '../defs.js';
import { netback } from '../production.js';
import { emit } from '../../core/bus.js';
import { spend, earn, notify, fmt$ } from '../util.js';
import { mk, peek, m4On, nid, r2, M4_FROM, resDefOf } from './marketCore.js';
import { fieldPriceInfo, isNegativeDay, wtiToday, brentToday, fieldZoneId, zoneHub, isNorthAmericanHub, NEG_DAY } from './marketPrices.js';

export { isNegativeDay, NEG_DAY };
export const CUSHING_MAX_MMBBL = 10;
export const DAYS_PER_MONTH = 30.4;
// Front-to-second-month WTI contango ($/bbl/month, est.) outside the 2020 super-contango (NEGATIVE_PRICE table).
export const CONTANGO_M4 = [[2000, 0], [2001.9, 0.5], [2002.3, 0], [2008.9, 2.5], [2009.05, 4], [2009.4, 1], [2010, 0.6], [2011, 0],
  [2014.9, 1], [2015.1, 2], [2015.5, 0.8], [2016.1, 1.5], [2016.5, 0.5], [2017.5, 0], [2020.0, 0.3], [2020.7, 0.3], [2021, 0]];
export function contangoPerMonth(y) {
  const n = NEGATIVE_PRICE.contangoPerMonth;
  if (y >= n[0][0] && y <= n[n.length - 1][0]) return lerpAt(n, y);
  return Math.max(0, lerpAt(CONTANGO_M4, y));
}
// Forward price for delivery `months` out under today's contango (first 3 months full, then half).
export const cushingForward = (front, months, y) => front + contangoPerMonth(y) * (Math.min(3, months) + 0.5 * Math.max(0, months - 3));

// ---- (6) futures expiry ---------------------------------------------------------------------------------
export const m4FuturesSettle = (state) => (isNegativeDay(state) ? NEGATIVE_PRICE.settle : null);
// Every other contract, forward leg and put that day references the next month (≈ the Brent-like curve, positive).
export const m4HedgeSpot = (state) => (isNegativeDay(state) ? brentToday(state) : null);

// ---- (3) forced sale when storage overflows at a negative price ------------------------------------------------
export function m4Overflow(state, fieldId, rest) {
  if (!(rest > 0) || !m4On(state)) return rest;
  const info = fieldPriceInfo(state, fieldId);
  if (!(info.price < 0)) return rest;
  const M = mk(state);
  // a Cushing lease with spare room takes North American barrels first (pipeline to the hub)
  if (isNorthAmericanHub(info.hub)) {
    for (const L of M.cushing) {
      const room = L.capacityBbl - L.bbl;
      if (room <= 0 || state.day >= L.untilDay) continue;
      const x = Math.min(room, rest);
      L.bbl += x; L.ownBbl = (L.ownBbl ?? 0) + x; rest -= x;
      if (rest <= 1e-9) return 0;
    }
  }
  const nb = netback(state, fieldId, rest);
  const gross = rest * nb.gross; // negative: the seller pays
  earn(state, gross, 'revenue');
  spend(state, rest * (nb.transport + nb.region), 'transport', false);
  state.company.stats.bblSold += rest;
  state.company.stats.revenueTotal += gross;
  const N = negState(state);
  N.forcedBbl += rest; N.negSoldBbl += rest; N.paid += -gross;
  const f = state.fields[fieldId];
  if (!f.m4ForcedWarned) {
    f.m4ForcedWarned = state.day;
    notify(state, 'news', { fieldId, kind: 'market', level: 'warn', text: `${fieldDef(fieldId)?.name}: storage is full and the price is ${fmt$(nb.gross)}/bbl — the purchaser takes your crude and bills you for it. Shut wells in to stop paying.` });
  }
  return 0;
}

function negState(state) {
  const M = mk(state);
  M.neg ??= { day: NEG_DAY, exposureWells: 0, exposureBbl: 0, negSoldBbl: 0, forcedBbl: 0, paid: 0, announced: false, settled: false };
  return M.neg;
}

// North American exposure: producing wells or crude in storage on WTI-linked fields.
function naExposure(state) {
  let wells = 0, bbl = 0;
  for (const w of allWells(state)) if (w.status === 'PRODUCING' && w.prod && !w.ownerOperatorId && isNorthAmericanHub(zoneHub(fieldZoneId(w.fieldId)))) wells++;
  for (const f of Object.values(state.fields)) if (f.storage?.bbl > 0 && isNorthAmericanHub(zoneHub(fieldZoneId(f.id)))) bbl += f.storage.bbl;
  return { wells, bbl };
}

export function tickNegativePre(state) {
  if (!isNegativeDay(state)) return;
  const N = negState(state);
  if (N.announced) return;
  const ex = naExposure(state);
  N.exposureWells = ex.wells; N.exposureBbl = ex.bbl; N.announced = true;
  const price = NEGATIVE_PRICE.settle;
  try { emit('negativePrice', { day: state.day, price, pause: ex.wells > 0 || ex.bbl > 0 }); } catch (e) { /* UI errors never break the sim */ }
  if (ex.wells > 0 || ex.bbl > 0) {
    notify(state, 'news', { kind: 'market', level: 'major', pause: true,
      text: `WTI for May delivery settles at ${fmt$(price)} — below zero for the first time. Selling a barrel today costs you money; auto-sell is paused. Hold crude in storage, shut wells in, or lease tanks at Cushing.` });
  }
}

// After production & sales: count barrels that left at a negative price (manual shipments, queued sales).
export function tickNegativePost(state) {
  if (!isNegativeDay(state)) return;
  const N = negState(state);
  for (const f of Object.values(state.fields)) {
    if (!(f.shippedToday > 0)) continue;
    const info = fieldPriceInfo(state, f.id);
    if (info.price < 0) N.negSoldBbl += f.shippedToday;
  }
  N.negSoldBbl = r2(N.negSoldBbl);
}

// End of the day: the achievement stat.
export function tickNegativeEcon(state) {
  if (!isNegativeDay(state)) return;
  const N = negState(state);
  if (N.settled) return;
  N.settled = true;
  if (N.exposureWells > 0 || N.exposureBbl > 0) state.company.stats.negativeDayHeld = N.negSoldBbl > 0 ? 0 : 1;
}

// ---- (4) shut-in restart cost & shale damage -----------------------------------------------------------------
const isShaleReservoir = (w) => {
  const f = fieldDef(w.fieldId);
  const r = resDefOf(w.fieldId, w.reservoirId);
  return !!(r?.unconventional || r?.typeCurve || w.lateral || (f?.shale && !r));
};
export function tickShutIns(state) {
  const M = mk(state);
  for (const w of allWells(state)) {
    if (w.status === 'SHUT_IN' && w.prod?.shutIn && !w.ownerOperatorId && M.shutIns[w.id] == null) M.shutIns[w.id] = state.day;
  }
  for (const id of Object.keys(M.shutIns)) {
    const w = state.wells[id];
    if (!w || !w.prod) { delete M.shutIns[id]; continue; }
    if (w.status === 'SHUT_IN' && w.prod.shutIn) continue;
    const since = M.shutIns[id];
    delete M.shutIns[id];
    if (w.status !== 'PRODUCING' || state.day - since < 1) continue;
    const R = NEGATIVE_PRICE.rules;
    const cost = eraDollars(state, R.shutInRestartCostPerWell, R.baseYear);
    spend(state, cost, 'opex', false);
    w.cost = (w.cost ?? 0) + cost;
    if (isShaleReservoir(w)) {
      w.prod.qi = Math.round(w.prod.qi * (1 - R.shutInReservoirDamage) * 10) / 10;
      w.prod.damage = Math.round((w.prod.damage ?? 1) * (1 - R.shutInReservoirDamage) * 1e4) / 1e4; // for A's type-curve code
    }
    M.ledger.restarts = (M.ledger.restarts ?? 0) + cost;
  }
}

// ---- (5) Cushing contango leases ---------------------------------------------------------------------------------
export function cushingQuote(state, { mmbbl = 1, months = 1 } = {}) {
  const y = yf(state);
  const n = Math.max(1, Math.min(CUSHING_MAX_MMBBL, Math.round(Number(mmbbl) || 1)));
  const m = Math.max(1, Math.min(12, Math.round(Number(months) || 1)));
  const front = wtiToday(state);
  const fwd = cushingForward(front, m, y);
  const rentPerDay = dayRateM4('cushing_storage', y) * n;
  const days = Math.round(m * DAYS_PER_MONTH);
  const bbl = n * 1e6;
  return { mmbbl: n, months: m, days, front: r2(front), forward: r2(fwd), contango: r2(contangoPerMonth(y)), rentPerDay: Math.round(rentPerDay),
    capacityBbl: bbl, rentTotal: Math.round(rentPerDay * days), carryPerBbl: r2(fwd - front - rentPerDay * days / bbl), profitIfFilled: Math.round(bbl * (fwd - front) - rentPerDay * days) };
}

export function leaseCushingImpl(state, { mmbbl = 1, months = 1, fill = true } = {}) {
  if (yf(state) < M4_FROM) return { ok: false, msg: 'Cushing tank leases are an M4 (2000+) market.' };
  const q = cushingQuote(state, { mmbbl, months });
  const buy = fill ? q.front * q.capacityBbl : 0;
  const need = Math.max(0, buy) + q.rentPerDay * 30;
  if (state.company.cash < need) return { ok: false, msg: `You need ${fmt$(need)} (crude ${fmt$(Math.max(0, buy))} + a month's rent).` };
  const M = mk(state);
  if (fill) { if (buy >= 0) spend(state, buy, 'crudePurchases', false); else earn(state, -buy, 'revenue'); }
  const L = { id: nid(state, 'cu'), startDay: state.day, untilDay: state.day + q.days, capacityBbl: q.capacityBbl, bbl: fill ? q.capacityBbl : 0, ownBbl: 0,
    cost: r2(buy), front: q.front, forward: q.forward, rentPerDay: q.rentPerDay, rentPaid: 0, filled: !!fill };
  M.cushing.push(L);
  return { ok: true, msg: fill
    ? `Leased ${q.mmbbl} MMbbl at Cushing for ${q.months} month${q.months > 1 ? 's' : ''}, bought ${q.capacityBbl.toLocaleString('en-US')} bbl at ${fmt$(q.front)} and sold them forward at ${fmt$(q.forward)}. Rent ${fmt$(q.rentPerDay)}/day.`
    : `Leased ${q.mmbbl} MMbbl of empty tankage at Cushing for ${q.months} month${q.months > 1 ? 's' : ''} (${fmt$(q.rentPerDay)}/day). Overflow from your US fields goes there, sold forward at ${fmt$(q.forward)}.`, leaseId: L.id };
}

export function tickCushing(state) {
  const M = peek(state);
  if (!M?.cushing?.length) return;
  for (const L of [...M.cushing]) {
    if (state.day < L.untilDay) {
      spend(state, L.rentPerDay, 'storage', false);
      L.rentPaid += L.rentPerDay; M.ledger.storageRent += L.rentPerDay;
      continue;
    }
    const proceeds = L.bbl * L.forward;
    earn(state, proceeds, 'revenue');
    state.company.stats.bblSold += L.ownBbl ?? 0;
    const pnl = proceeds - L.cost - L.rentPaid;
    M.ledger.contango += pnl;
    M.cushing = M.cushing.filter((x) => x !== L);
    notify(state, 'news', { kind: 'finance', text: `Cushing lease ${L.id} delivered ${Math.round(L.bbl).toLocaleString('en-US')} bbl at ${fmt$(L.forward)}: ${pnl >= 0 ? 'profit' : 'loss'} ${fmt$(Math.abs(pnl))} after ${fmt$(L.rentPaid)} rent.` });
  }
}

export function negativeDayViewImpl(state) {
  const N = peek(state)?.neg ?? null;
  const y = yf(state);
  const daysTo = NEG_DAY - state.day;
  return {
    day: NEG_DAY, today: isNegativeDay(state), daysTo, past: daysTo < 0, settle: NEGATIVE_PRICE.settle, contract: NEGATIVE_PRICE.expiryContract,
    cushingUtil: y >= 2020 && y < 2021 ? r2(lerpAt(NEGATIVE_PRICE.cushingUtil, y)) : null, contango: r2(contangoPerMonth(y)),
    rules: { ...NEGATIVE_PRICE.rules }, wellheadDiscounts: { ...NEGATIVE_PRICE.rules.wellheadDiscountFromCushing },
    status: N ? { exposureWells: N.exposureWells, exposureBbl: Math.round(N.exposureBbl), negSoldBbl: Math.round(N.negSoldBbl), forcedBbl: Math.round(N.forcedBbl), paid: Math.round(N.paid), held: state.company.stats.negativeDayHeld ?? null } : null,
    leases: (peek(state)?.cushing ?? []).map((L) => ({ ...L, daysLeft: Math.max(0, L.untilDay - state.day), mtm: Math.round(L.bbl * (L.forward) - L.cost - L.rentPaid - L.rentPerDay * Math.max(0, L.untilDay - state.day)) })),
    quote: m4On(state) ? cushingQuote(state, { mmbbl: 1, months: 1 }) : null,
    shutInRestartCost: m4On(state) ? Math.round(eraDollars(state, NEGATIVE_PRICE.rules.shutInRestartCostPerWell, NEGATIVE_PRICE.rules.baseYear)) : null,
  };
}
